import { useState } from 'react';
import { Link } from 'react-router';
import { RelativeTime } from '@/components/RelativeTime';
import { VPN_FLOWS } from '@/features/vpnFlows';
import type { GatewayVpn, VpnSession } from '../api';
import { userLabel } from '../format';
import { useGatewayVpn, useVpnSessions } from '../hooks';
import { GatewayVpnBadge } from './Badges';
import { Card, Facts } from './Facts';
import { SessionSheet } from './SessionSheet';

function humanize(value: string): string {
  return value.replace(/_/g, ' ');
}

/** Its region's setting, which it follows, and whether it can run the VPN. */
function Status({ vpn }: { vpn: GatewayVpn }) {
  return (
    <Card
      title="VPN"
      description={
        <>
          This gateway follows its region,{' '}
          <Link className="underline" to={`/admin/vpn/sites?region=${vpn.regionId}`}>
            {vpn.regionName}
          </Link>
          , where the VPN is {vpn.enabled ? 'on' : 'off'}. The switch and the site&rsquo;s DNS servers are there.
        </>
      }
      action={<GatewayVpnBadge vpn={vpn} />}
    >
      {vpn.available ? (
        <p className="text-xs text-muted-foreground">It can run the VPN, and carry sessions to its region.</p>
      ) : (
        <p className="text-xs text-sev-1">{vpn.unavailableReason ?? "It can't run the VPN."}</p>
      )}
      {vpn.report?.warnings?.length ? (
        <div className="mt-2">
          <p className="text-xs text-sev-2">Its host may still stop the VPN&rsquo;s traffic:</p>
          <ul className="mt-1 list-disc pl-4 space-y-0.5">
            {vpn.report.warnings.map((warning) => (
              <li key={warning} className="text-xs text-sev-2">
                {warning}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

function Report({ vpn }: { vpn: GatewayVpn }) {
  const report = vpn.report;
  if (!report) {
    return (
      <Card title="What the gateway reports">
        <p className="text-xs text-muted-foreground">
          It hasn&rsquo;t reported on its VPN. A gateway reports once its software includes the VPN.
        </p>
      </Card>
    );
  }
  return (
    <Card title="What the gateway reports" action={<RelativeTime value={report.reportedAt} />}>
      <Facts
        items={[
          ['VPN at the gateway', report.on ? 'On' : 'Off'],
          ['Error', report.error],
          ['Listen port', report.listenPort],
          ['Public key', report.publicKey ? <code className="text-xs break-all">{report.publicKey}</code> : null],
          ['NAT behaviour', humanize(report.natBehaviour)],
          [
            'Candidates',
            report.candidates.length ? (
              <ul className="space-y-0.5">
                {report.candidates.map((c) => (
                  <li key={c.address}>
                    <code className="text-xs">{c.address}</code>{' '}
                    <span className="text-xs text-muted-foreground">{humanize(c.kind)}</span>
                  </li>
                ))}
              </ul>
            ) : null,
          ],
          [
            'STUN servers',
            report.stunAnswers.length ? (
              <ul className="space-y-0.5">
                {report.stunAnswers.map((a) => (
                  <li key={a.server} className="text-xs">
                    <code>{a.server}</code> → {a.error ? <span className="text-sev-1">{a.error}</span> : <code>{a.mapped}</code>}
                  </li>
                ))}
              </ul>
            ) : null,
          ],
          ['Resolver forwards to', report.dnsServers.join(', ') || null],
          ['Resolver error', report.dnsError],
          ['Sessions it holds', report.sessions],
          ...(VPN_FLOWS
            ? [['Flow records', `${String(report.flowsQueued)} waiting to upload · ${String(report.flowsLost)} lost since it started`] as const]
            : []),
          ['Its clock', <RelativeTime key="clock" value={report.gatewayTime} absolute />],
        ]}
      />
    </Card>
  );
}

function LiveSessions({ gatewayId }: { gatewayId: string }) {
  const { data } = useVpnSessions({ gatewayId, live: true, pageSize: 20 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const sessions: VpnSession[] = data?.sessions ?? [];
  const selected = sessions.find((s) => s.id === selectedId) ?? null;
  return (
    <Card
      title="Live sessions"
      action={
        <Link className="text-xs underline" to={`/vpn/sessions?gatewayId=${gatewayId}`}>
          Every session here
        </Link>
      }
    >
      {sessions.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nobody is connected through this gateway.</p>
      ) : (
        <ul className="divide-y divide-border">
          {sessions.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="w-full text-left py-2 first:pt-0 last:pb-0 text-sm flex justify-between gap-3"
                onClick={() => { setSelectedId(s.id); }}
              >
                <span className="truncate">
                  {userLabel(s)}
                  {s.clientDeviceName ? <span className="text-muted-foreground"> · {s.clientDeviceName}</span> : null}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  until <RelativeTime value={s.expiresAt} />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <SessionSheet session={selected} onOpenChange={(open) => { if (!open) setSelectedId(null); }} />
    </Card>
  );
}

/** A gateway's VPN, on its page (`/devices/:id?tab=vpn`): its own state. The
 *  setting is its region's. */
export function GatewayVpnTab({ gatewayId }: { gatewayId: string }) {
  const { data: vpn, isLoading, isError } = useGatewayVpn(gatewayId);
  if (isLoading) return <p className="text-xs text-muted-foreground">Loading…</p>;
  if (isError || !vpn) return <p className="text-xs text-muted-foreground">Couldn&rsquo;t load this gateway&rsquo;s VPN.</p>;
  return (
    <div className="space-y-6 max-w-3xl">
      <Status vpn={vpn} />
      <Report vpn={vpn} />
      <LiveSessions gatewayId={gatewayId} />
    </div>
  );
}
